import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatDateBR, formatCPF } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, Loader2, Check, X, Minus } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const LOGO_URL =
    "https://customer-assets-eiarnc6j.emergentagent.net/job_carpool-manager-6/artifacts/qyxqg7dd_logo%20Padr%C3%A3o.png";

const STATUS_SYMBOL = {
    ok: { icon: Check, label: "OK", cls: "text-green-700" },
    nao_ok: { icon: X, label: "Não OK", cls: "text-red-700 font-bold" },
    na: { icon: Minus, label: "N/A", cls: "text-gray-500" },
};

export default function InspectionPrint() {
    const { id } = useParams();
    const { user } = useAuth();
    const [insp, setInsp] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        api.get(`/inspections/${id}`).then((res) => {
            setInsp(res.data);
            setLoading(false);
        }).catch(() => { setError(true); setLoading(false); });
    }, [id]);

    if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
    if (error || !insp) {
        return (
            <div className="min-h-screen flex items-center justify-center flex-col gap-4">
                <div className="text-muted-foreground">Inspeção não encontrada</div>
                <Button onClick={() => window.close()}>Fechar</Button>
            </div>
        );
    }

    // Group items by categoria
    const byCategoria = {};
    (insp.itens || []).forEach((it) => {
        const cat = it.categoria || "Outros";
        if (!byCategoria[cat]) byCategoria[cat] = [];
        byCategoria[cat].push(it);
    });

    const naoOk = (insp.itens || []).filter((i) => i.status === "nao_ok");
    const today = new Date().toLocaleDateString("pt-BR");

    return (
        <div className="print-root min-h-screen bg-gray-100 p-8" data-testid="inspection-print-page">
            <div className="no-print max-w-3xl mx-auto mb-4 flex items-center justify-between">
                <Button variant="outline" onClick={() => window.close()}>Fechar</Button>
                <Button onClick={() => window.print()} className="gap-2" data-testid="btn-print-insp-now">
                    <Printer className="w-4 h-4" /> Imprimir
                </Button>
            </div>

            <div className="ficha max-w-3xl mx-auto bg-white shadow-md text-sm">
                <div className="flex items-center gap-4 p-6 border-b-2 border-black">
                    <div className="w-20 h-20 bg-emerald-900 rounded-lg flex items-center justify-center p-1 shrink-0 border-2 border-emerald-800 shadow-md">
                        <img src={LOGO_URL} alt="COOMFLONA" className="w-full h-full object-contain" />
                    </div>
                    <div className="flex-1 text-center">
                        <div className="font-extrabold text-lg leading-tight">COOPERATIVA MISTA DA FLONA DO TAPAJÓS – COOMFLONA</div>
                        <div className="text-xs mt-1">CNPJ: 07.315.165/0001-00</div>
                    </div>
                </div>

                <div className="text-center py-3 border-b border-black">
                    <div className="font-extrabold text-base">CHECKLIST DE VEÍCULOS</div>
                    <div className="text-xs">Inspeção Visual do Veículo — {insp.tipo_viagem === "chegada" ? "Chegada" : "Saída"}</div>
                </div>

                <div className="p-6 space-y-3">
                    <div className="grid grid-cols-3 gap-4 text-sm">
                        <FieldMini label="Veículo" value={insp.veiculo_modelo || "—"} />
                        <FieldMini label="Placa" value={insp.veiculo_placa} />
                        <FieldMini label="KM atual" value={insp.km_atual != null ? `${insp.km_atual.toLocaleString("pt-BR")} km` : "—"} />
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                        <FieldMini label="Motorista" value={insp.motorista_nome} />
                        <FieldMini label="CPF" value={formatCPF(insp.cpf) || "—"} />
                        <FieldMini label="Combustível" value={insp.combustivel_nivel || "—"} />
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                        <FieldMini label="Data" value={formatDateBR(insp.data)} />
                        <FieldMini label="Hora" value={insp.hora || "—"} />
                    </div>

                    {/* Resumo geral */}
                    <div className={`mt-3 p-3 rounded border-2 ${naoOk.length === 0 ? "border-green-600 bg-green-50" : "border-red-600 bg-red-50"}`}>
                        <div className="font-bold">
                            {naoOk.length === 0
                                ? "✓ Veículo em condições normais — todos os itens OK"
                                : `⚠ Atenção: ${naoOk.length} ${naoOk.length === 1 ? "item" : "itens"} necessitam atenção`}
                        </div>
                    </div>

                    {/* Checklist por categoria */}
                    {Object.entries(byCategoria).map(([categoria, items]) => (
                        <div key={categoria} className="mt-4">
                            <div className="bg-gray-800 text-white text-xs font-bold uppercase px-3 py-1 rounded-t">{categoria}</div>
                            <table className="w-full border border-gray-400 text-xs">
                                <thead>
                                    <tr className="bg-gray-100">
                                        <th className="text-left px-2 py-1 border-r border-gray-400 w-[60%]">Item</th>
                                        <th className="text-center px-2 py-1 border-r border-gray-400 w-[15%]">Status</th>
                                        <th className="text-left px-2 py-1">Observação</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((item, i) => {
                                        const s = STATUS_SYMBOL[item.status] || STATUS_SYMBOL.na;
                                        const Icon = s.icon;
                                        return (
                                            <tr key={i} className="border-t border-gray-300">
                                                <td className="px-2 py-1 border-r border-gray-300">{item.label}</td>
                                                <td className="px-2 py-1 border-r border-gray-300 text-center">
                                                    <span className={`inline-flex items-center gap-1 ${s.cls}`}>
                                                        <Icon className="w-3 h-3 inline" /> {s.label}
                                                    </span>
                                                </td>
                                                <td className="px-2 py-1 italic text-gray-700">{item.observacao || "—"}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ))}

                    {insp.observacoes_gerais && (
                        <div className="mt-4 border-t border-gray-300 pt-3">
                            <div className="text-xs uppercase tracking-wider text-gray-600 font-semibold">Observações gerais</div>
                            <div className="mt-1 text-sm whitespace-pre-wrap border border-gray-300 rounded p-2">{insp.observacoes_gerais}</div>
                        </div>
                    )}
                </div>

                <div className="px-6 pb-6 pt-10 grid grid-cols-2 gap-6">
                    <div className="text-center">
                        <div className="border-t border-black pt-2 text-sm font-medium">Motorista / Responsável</div>
                        <div className="text-xs text-gray-600 mt-1">{insp.motorista_nome}</div>
                    </div>
                    <div className="text-center">
                        <div className="border-t border-black pt-2 text-sm font-medium">Setor de Transporte</div>
                        <div className="text-xs text-gray-600 mt-1">{insp.inspecionado_por || user?.name || "—"}</div>
                    </div>
                </div>

                <div className="px-6 pb-3 flex justify-between text-[10px] text-gray-500 border-t border-gray-300 pt-2">
                    <div>Emitido em: {today} {user?.name ? `— por ${user.name}` : ""}</div>
                    <div>Inspeção nº: {insp.id.slice(0, 8).toUpperCase()}</div>
                </div>
            </div>

            <style>{`
                @media print {
                    body { background: white !important; }
                    .no-print { display: none !important; }
                    .print-root { padding: 0 !important; background: white !important; }
                    .ficha { box-shadow: none !important; max-width: 100% !important; }
                    @page { size: A4; margin: 12mm; }
                }
            `}</style>
        </div>
    );
}

function FieldMini({ label, value }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-600 font-semibold">{label}</div>
            <div className="font-medium border-b border-dashed border-gray-400 pb-0.5 min-h-[18px]">{value || "—"}</div>
        </div>
    );
}
